import React, { useEffect, useMemo, useState } from 'react';
import { Chip, Divider, Input } from '@peoplegrove/pg-components';
import {
  PERIOD_PRESETS,
  getMatchingPresetKey,
  getPresetPeriod,
  getMinSelectableMonth,
  getMaxSelectableMonth,
  isValidPeriodRange,
  normalizeMonthValue,
  normalizePeriodRange,
  clampPeriodToMinMonth,
} from '../periodUtils';
import s from '../AnnualReviewReport.module.scss';

const PeriodRangePicker = ({ value, onChange, hubCreatedAt }) => {
  const minMonth = useMemo(
    () => getMinSelectableMonth(hubCreatedAt),
    [hubCreatedAt]
  );
  const maxMonth = useMemo(() => getMaxSelectableMonth(), []);
  const [draft, setDraft] = useState(value);
  const activePreset = useMemo(
    () => getMatchingPresetKey(value, minMonth),
    [value, minMonth]
  );

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const emitChange = nextPeriod => {
    const normalized = normalizePeriodRange(nextPeriod);
    if (!normalized) return;
    const clamped = clampPeriodToMinMonth(normalized, minMonth);
    if (!isValidPeriodRange(clamped, minMonth)) return;
    onChange(clamped);
  };

  const handlePresetClick = months => {
    emitChange(getPresetPeriod(months, minMonth));
  };

  const handleMonthChange = (field, nextValue) => {
    const month = normalizeMonthValue(nextValue);
    if (!month) return;
    if (minMonth && month < minMonth) return;
    if (maxMonth && month > maxMonth) return;
    const nextPeriod = {
      start: field === 'start' ? month : draft?.start,
      end: field === 'end' ? month : draft?.end,
    };
    setDraft(nextPeriod);
    emitChange(nextPeriod);
  };

  return (
    <div className={s.periodSelector}>
      <div className={s.periodSection}>
        <h4 className={s.periodSectionTitle}>Preset Date Ranges</h4>
        <div
          className={s.presetList}
          role="group"
          aria-label="Preset date ranges"
        >
          {PERIOD_PRESETS.map(preset => {
            const isActive = activePreset === preset.key;
            return (
              <Chip
                key={preset.key}
                text={preset.label}
                button
                showBorder
                color={isActive ? 'cyan' : 'transparent'}
                className={
                  isActive ? s.presetChipActive : s.presetChipInactive
                }
                aria-label={preset.label}
                aria-pressed={isActive}
                onClick={() => handlePresetClick(preset.months)}
              />
            );
          })}
        </div>
      </div>

      <Divider text="OR" className={s.periodDivider} />

      <div className={s.periodSection}>
        <h4 className={s.periodSectionTitle}>Custom Month Range</h4>
        <p className={s.periodHelper}>
          Pick a start and end month — data always spans whole months.
          {minMonth
            ? ` Earliest available month is based on hub creation (${minMonth}).`
            : ''}
        </p>
        <div className={s.customMonthRow}>
          <Input
            id="period-start-month"
            type="month"
            size="small"
            icon="pg-calendar"
            min={minMonth || undefined}
            max={maxMonth}
            value={draft?.start ?? ''}
            onChange={val => handleMonthChange('start', val)}
            aria-label="Start month"
            className={s.monthInput}
          />
          <span className={s.monthRangeSeparator} aria-hidden="true">
            ~
          </span>
          <Input
            id="period-end-month"
            type="month"
            size="small"
            icon="pg-calendar"
            min={minMonth || undefined}
            max={maxMonth}
            value={draft?.end ?? ''}
            onChange={val => handleMonthChange('end', val)}
            aria-label="End month"
            className={s.monthInput}
          />
        </div>
      </div>
    </div>
  );
};

export default PeriodRangePicker;
